"use strict";

exports.__esModule = true;
exports.BreakType = exports.AdBreak = void 0;
exports.normalisePosition = normalisePosition;
var _YoLog = require("../Utils/YoLog");
var _AdBreakEventHandler2 = require("../Public/AdBreakEventHandler");
var _Advert = require("./Advert");
var _XmlNode = require("../Core/XmlNode");
var _StringUtils = require("../Utils/StringUtils");
function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (it) return (it = it.call(o)).next.bind(it); if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }
/**
 * Defines the possible ad break types as defined by the IAB VMAP specification
 * @enum {number}
 */
var BreakType = Object.freeze({
  /** Linear break type */
  LINEAR: 0,
  /** Non-linear break type */
  NONLINEAR: 1,
  /** Display break type */
  DISPLAY: 2
});
exports.BreakType = BreakType;
function normalisePosition(position, start, breakType) {
  var pos = position ? position.toLowerCase() : "";
  if (pos === "preroll" || pos === "midroll" || pos === "postroll") {
    return pos;
  } else if (start === 0) {
    return "preroll";
  } else {
    // The position attribute may only ever be empty in a VOD stream with CSM < 2.40 or for
    // non-linear breaks. There is only a very small chance it could be postroll with these conditions
    if (breakType === BreakType.LINEAR) {
      return "midroll";
    } else {
      return "unknown";
    }
  }
}

/**
 * Represents an ad break object.
 *
 * The AdBreak encapsulates the data from the `<vmap:AdBreak>` element of a VMAP document: its
 * attributes, adverts and positional information.
 *
 * Through {@link AdBreakEventHandler} it supports the ability for a client app to signal non-linear events.
 * 
 * @hideconstructor
 * @see {@link AdBreakEventHandler}, which this class implements
 */
var AdBreak = /*#__PURE__*/function (_AdBreakEventHandler) {
  _inheritsLoose(AdBreak, _AdBreakEventHandler);
  function AdBreak(start, duration, position, trackingMap, breakType, identifier, extensions, adTagUri) {
    var _this;
    _this = _AdBreakEventHandler.call(this) || this;

    /** @private */
    _this.startMillis = start;
    /** @private */
    _this.duration = duration;
    /** @private */
    _this.trackingMap = trackingMap ? trackingMap : new Map();
    /** @private */
    _this.breakType = AdBreak.getBreakTypeFromString(breakType);
    /** @private */
    _this.position = normalisePosition(position, start, _this.breakType);
    /** @private */
    _this.identifier = identifier;
    /** @private */
    _this.extensions = extensions;
    /** @private */
    _this.broker = null;
    /** @private */
    _this.adverts = [];
    /** @private */
    _this.adTagUri = adTagUri ? adTagUri : null;
    if (_this.breakType !== BreakType.LINEAR && _this.startMillis === -1) {
      _this.startMillis = 0;
    }
    return _this;
  }
  AdBreak.getBreakTypeFromString = function getBreakTypeFromString(type) {
    if ((0, _StringUtils.equalsIgnoreCase)(type, "nonlinear")) {
      return BreakType.NONLINEAR;
    } else if ((0, _StringUtils.equalsIgnoreCase)(type, "display")) {
      return BreakType.DISPLAY;
    } else {
      return BreakType.LINEAR;
    }
  };
  var _proto = AdBreak.prototype;
  _proto.adjustForEarlyReturn = function adjustForEarlyReturn(playhead) {
    var ad = this.getAdvertFromPlayhead(playhead);
    if (ad) {
      var onBoundary = ad.getStart() === playhead;
      if (!onBoundary) {
        ad.adjustForEarlyReturn(playhead);
      }
      this.removeAdvertsFrom(ad, onBoundary);
      this.setDuration(playhead - this.startMillis);
      return true;
    }
    return false;
  };
  _proto.getAdvertForCreative = function getAdvertForCreative(creative) {
    for (var _iterator = _createForOfIteratorHelperLoose(this.adverts), _step; !(_step = _iterator()).done;) {
      var ad = _step.value;
      if (ad.containsCreative(creative)) {
        return ad;
      }
    }
    return null;
  };
  _proto.getAdvertFromPlayhead = function getAdvertFromPlayhead(playhead) {
    for (var _iterator2 = _createForOfIteratorHelperLoose(this.adverts), _step2; !(_step2 = _iterator2()).done;) {
      var ad = _step2.value;
      if (ad.getStart() <= playhead && playhead < ad.getEnd()) {
        return ad;
      }
    }
    return null;
  };
  _proto.getAdvertFromIdentifier = function getAdvertFromIdentifier(mediaId) {
    for (var _iterator3 = _createForOfIteratorHelperLoose(this.adverts), _step3; !(_step3 = _iterator3()).done;) {
      var ad = _step3.value;
      if (ad.getMediaIdentifier() === mediaId) {
        return ad;
      }
    }
    return null;
  }

  /**
   *
   * @return {Advert[]} an array of all `Advert` objects in this ad break
   */;
  _proto.getAdverts = function getAdverts() {
    return Object.assign([], this.adverts);
  };
  _proto.getAdTagUri = function getAdTagUri() {
    return this.adTagUri;
  };
  _proto.getBreakTypeAsString = function getBreakTypeAsString() {
    switch (this.breakType) {
      case BreakType.NONLINEAR:
        return "nonlinear";
      case BreakType.DISPLAY:
        return "display";
      default:
        return "linear";
    }
  }

  /**
   *
   * @return {BreakType} the break type of the ad break. Possible values are LINEAR, NONLINEAR and DISPLAY.
   */;
  _proto.getType = function getType() {
    return this.breakType;
  }

  /**
   * @return {number} the duration of the ad break, in milliseconds
   */;
  _proto.getDuration = function getDuration() {
    return this.duration;
  }

  /**
   *
   * @return {XmlNode} the XML VAST extensions for this ad break as an `XmlNode` or null if there are no extensions.
   */;
  _proto.getExtensions = function getExtensions() {
    return this.extensions;
  }

  /**
   * @return {string} the identifier of the ad break
   */;
  _proto.getIdentifier = function getIdentifier() {
    return this.identifier;
  }

  /**
   * @return {boolean} true if the break is a placeholder break
   */;
  _proto.isPlaceholder = function isPlaceholder() {
    return this.adTagUri !== null;
  }

  /**
   * Returns the position of the ad break in the stream, which may be one of:
   * 
   * - `preroll`
   * - `midroll`
   * - `postroll`
   * - `unknown`
   * 
   * @return {string} the position of the ad break as a `string`.
   */;
  _proto.getPosition = function getPosition() {
    return this.position;
  }

  /** Returns the amount of natural time remaining for the ad break from the given playhead position.
   * The value returned has a maximum value of the advert duration and a minimum value of zero.
   * 
   * @param {number} playhead The playhead position, in milliseconds.
   * @return {number} the time remaining in the ad break from the playhead provided, in milliseconds
   */;
  _proto.getRemainingTime = function getRemainingTime(playhead) {
    var delta = 0;
    if (this.adverts.length > 0) {
      var advert = this.adverts[0];
      var end = advert.getStart() + this.getDuration();
      delta = Math.min(end - playhead, this.getDuration());
    }
    return Math.max(0, delta);
  }

  /**
   * @return {number} the start position in milliseconds
   */;
  _proto.getStart = function getStart() {
    return this.startMillis;
  };
  _proto.getTrackingMap = function getTrackingMap() {
    return this.trackingMap;
  };
  _proto.getTrackingReport = function getTrackingReport(type, shouldRemove) {
    var report = this.trackingMap.get(type);
    if (report && shouldRemove) {
      this.trackingMap["delete"](type);
    }
    return report;
  }

  /** Returns whether the ad break is active, that is, whether any of its adverts have not yet been played.
   * @return {boolean} true if the ad break is active, false otherwise.
   */;
  _proto.isActive = function isActive() {
    if (!this.adverts) {
      return false;
    }
    for (var _iterator4 = _createForOfIteratorHelperLoose(this.adverts), _step4; !(_step4 = _iterator4()).done;) {
      var ad = _step4.value;
      if (ad.isActive()) {
        return true;
      }
    }
    return false;
  };
  _proto.isNonLinear = function isNonLinear() {
    return this.breakType !== BreakType.LINEAR;
  };
  _proto.isValid = function isValid() {
    if (this.breakType === BreakType.LINEAR && this.startMillis === -1) {
      _YoLog.YoLog.e("Linear AdBreak is invalid - start position is unknown");
      return false;
    }
    return this.adverts.length > 0 || this.trackingMap.size > 0 || this.isPlaceholder();
  };
  _proto.onNonLinearTrackingEvent = function onNonLinearTrackingEvent(event) {
    if (!this.isNonLinear()) {
      _YoLog.YoLog.w("Warning: AdBreak is not nonlinear - can't signal NonLinearTrackingEvent");
      return;
    }
    var report = this.getTrackingReport(event, false);
    if (report) {
      this.broker.fireTrackingReport(report);
    }
  };
  _proto.postParse = function postParse(broker) {
    this.broker = broker;
    var offset = this.startMillis;
    for (var _iterator5 = _createForOfIteratorHelperLoose(this.adverts), _step5; !(_step5 = _iterator5()).done;) {
      var ad = _step5.value;
      ad.setStart(offset);
      offset += ad.getDuration();
      ad.postParse(broker);
    }
  }

  // Remove adverts following the 'advert' param, if found.  Also removes the advert itself if inclusive === true
  ;
  _proto.removeAdvertsFrom = function removeAdvertsFrom(advert, inclusive) {
    for (var i = 0; i < this.adverts.length; ++i) {
      var ad = this.adverts[i];
      if (ad === advert) {
        var index = inclusive === true ? i : i + 1;
        this.adverts.splice(index, this.adverts.length - index);
        return;
      }
    }
  };
  _proto.setAdverts = function setAdverts(adverts) {
    Array.prototype.push.apply(this.adverts, adverts);
    var offset = this.startMillis;
    for (var _iterator6 = _createForOfIteratorHelperLoose(this.adverts), _step6; !(_step6 = _iterator6()).done;) {
      var advert = _step6.value;
      advert.setStart(offset);
      offset += advert.getDuration();
    }
  }

  /*  Returns false is not all adverts were set inactive.  Otherwise true. */;
  _proto.setAdvertsInactivePriorTo = function setAdvertsInactivePriorTo(playhead) {
    for (var _iterator7 = _createForOfIteratorHelperLoose(this.adverts), _step7; !(_step7 = _iterator7()).done;) {
      var ad = _step7.value;
      if (ad.getEnd() < playhead) {
        ad.setInactive();
      } else {
        return false;
      }
    }
    return true;
  };
  _proto.setDuration = function setDuration(duration) {
    this.duration = duration;
  }

  /**
   * Sets the ad break inactive such that no analytic beacons will fire. The only client Callbacks
   * that will be raised are `onAdBreakStart` and `onAdBreakEnd`.
   */;
  _proto.setInactive = function setInactive() {
    // If ad break is non-linear, ads are always active
    if (this.isNonLinear()) {
      return;
    }
    for (var _iterator8 = _createForOfIteratorHelperLoose(this.adverts), _step8; !(_step8 = _iterator8()).done;) {
      var ad = _step8.value;
      ad.setInactive();
    }
    this.trackingMap.clear();
  };
  _proto.toString = function toString() {
    return "\n--- AdBreak ---\n breakType:" + this.getBreakTypeAsString() + " start:" + this.startMillis + " milliseconds, duration:" + this.duration + ", position:" + this.position + "\nNumber of adverts:" + this.adverts.length;
  };
  return AdBreak;
}(_AdBreakEventHandler2.AdBreakEventHandler);
exports.AdBreak = AdBreak;